/**
 * SaveDestinationDialog — "Where should this go?" (proposal 8, kanban
 * 5953f066). Replaces the Save-as-investigation drop-down: the person picks
 * a new case (title editable, never truncated) or one of the open cases for
 * the SAME customer (full number and title, stage, who it waits on), with
 * Merlin's one-line recommendation above. The primary button names the
 * action. Escape or Cancel closes it; Tab stays inside while it is open.
 */
import { useEffect, useRef, useState } from 'react';

import type { DoorInvestigationRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import {
  candidateMeta,
  recommendationLine,
  type SaveDestination,
} from './saveDestinationView';
import styles from './SaveDestinationDialog.module.css';

export type SaveChoice =
  | { kind: 'new'; title: string }
  | { kind: 'attach'; investigation: DoorInvestigationRead };

export interface SaveDestinationDialogProps {
  destination: SaveDestination;
  /** The door's asks, for each case's "Their court · …". */
  asks: DoorDataRequestRead[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  /** The radio changed (for the audit trail). */
  onChoose: (kind: 'new' | 'attach', investigation: DoorInvestigationRead | null) => void;
  onConfirm: (choice: SaveChoice) => void;
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SaveDestinationDialog({
  destination,
  asks,
  busy,
  error,
  onCancel,
  onChoose,
  onConfirm,
}: SaveDestinationDialogProps) {
  const [picked, setPicked] = useState<string>('new');
  const [title, setTitle] = useState(destination.proposedTitle);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  // Focus into the dialog on open: the selected choice.
  useEffect(() => {
    firstRadioRef.current?.focus();
  }, []);

  const chosen =
    picked === 'new' ? null : destination.candidates.find((c) => c.id === picked) ?? null;
  const canConfirm = !busy && (chosen !== null || title.trim().length > 0);
  const primaryLabel = busy
    ? 'Saving…'
    : chosen
      ? `Add to ${chosen.investigation_no}`
      : 'Create the new case';

  const pick = (id: string) => {
    setPicked(id);
    const inv = id === 'new' ? null : destination.candidates.find((c) => c.id === id) ?? null;
    onChoose(inv ? 'attach' : 'new', inv);
  };

  const confirm = () => {
    if (!canConfirm) return;
    onConfirm(chosen ? { kind: 'attach', investigation: chosen } : { kind: 'new', title: title.trim() });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onCancel();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      // Only the checked radio of a group takes a Tab stop.
      (n) => !(n instanceof HTMLInputElement && n.type === 'radio' && !n.checked),
    );
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={() => !busy && onCancel()}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-destination-title"
        aria-describedby="save-destination-subject"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <h2 id="save-destination-title" className={styles.title}>
          Where should this go?
        </h2>
        {destination.subject && (
          <p id="save-destination-subject" className={styles.subject}>
            {destination.subject}
          </p>
        )}
        <p className={styles.read} data-testid="save-destination-read">
          {recommendationLine(destination.recommendation)}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirm();
          }}
        >
          <fieldset className={styles.choices} disabled={busy}>
            <legend className={styles.srOnly}>Save this conversation as</legend>
            <div className={`${styles.choice} ${picked === 'new' ? styles.choiceOn : ''}`}>
              <input
                ref={firstRadioRef}
                id="save-destination-new"
                type="radio"
                name="save-destination"
                value="new"
                checked={picked === 'new'}
                onChange={() => pick('new')}
              />
              <span className={styles.choiceBody}>
                <label htmlFor="save-destination-new" className={styles.newLabel}>
                  New case
                </label>
                <span className={styles.titleField}>
                  <label htmlFor="save-destination-new-title" className={styles.titleLabel}>
                    Title
                  </label>
                  <input
                    id="save-destination-new-title"
                    className={styles.titleInput}
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      // Typing a title means a new case.
                      if (picked !== 'new') pick('new');
                    }}
                  />
                </span>
              </span>
            </div>

            {destination.candidates.length > 0 && (
              <div className={styles.orHead}>Or add it to an open case for the same customer</div>
            )}
            {destination.candidates.map((inv) => (
              <label
                key={inv.id}
                className={`${styles.choice} ${picked === inv.id ? styles.choiceOn : ''}`}
              >
                <input
                  type="radio"
                  name="save-destination"
                  value={inv.id}
                  checked={picked === inv.id}
                  onChange={() => pick(inv.id)}
                />
                <span className={styles.choiceBody}>
                  <span className={styles.caseTitle}>
                    <b>{inv.investigation_no}</b> {inv.title}
                  </span>
                  <span className={styles.caseMeta}>{candidateMeta(inv, asks)}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className={styles.primary} disabled={!canConfirm}>
              {primaryLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SaveDestinationDialog;
